import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageShell from "@/components/proof/PageShell";
import {
    BlinkingDot,
    MonoLabel,
    FieldButton,
} from "@/components/proof/primitives";
import { easing } from "@/lib/animations";

const HIGHLIGHTS = [
    {
        label: "Domestic · Poland",
        copy: "Z atelier do paczkomatu w 24h. InPost Paczkomat od 14,90 PLN.",
        rate: "14,90 PLN",
    },
    {
        label: "Free over 399 PLN",
        copy: "Każda paczka wysłana w Polsce powyżej 399 PLN — dostawa na nasz koszt.",
        rate: "Free",
    },
];

const ZONES = [
    {
        n: "01",
        label: "Zone 1 · Central Europe",
        countries: ["Czechia", "Germany", "Slovakia", "Hungary", "Romania"],
        carrier: "DPD · GLS",
        rate: "8 EUR",
        leadTime: "2–4 business days",
    },
    {
        n: "02",
        label: "Zone 2 · Western & Northern Europe",
        countries: [
            "France",
            "Italy",
            "Netherlands",
            "Belgium",
            "Austria",
            "Denmark",
            "Sweden",
            "Finland",
            "Ireland",
            "Portugal",
            "Spain",
        ],
        carrier: "DPD · DHL",
        rate: "14 EUR",
        leadTime: "3–6 business days",
    },
    {
        n: "03",
        label: "Zone 3 · United Kingdom",
        countries: ["United Kingdom"],
        carrier: "DHL Express",
        rate: "22 EUR",
        leadTime: "3–5 business days",
        note: "Duties payable on arrival.",
    },
    {
        n: "04",
        label: "Zone 4 · Rest of World",
        countries: ["United States", "Canada", "Japan", "Korea", "Australia", "UAE"],
        carrier: "DHL Express",
        rate: "32 EUR",
        leadTime: "5–9 business days",
        note: "Local duties + VAT on arrival.",
    },
];

const NOTES = [
    {
        h: "Packed by hand",
        p: "Każda paczka pakowana jest ręcznie w atelier na Próżnej w Warszawie. Tape, no plastic. Pojedyncza monodrukowana karta z podziękowaniem w każdym kartonie.",
    },
    {
        h: "Pre-orders & drops",
        p: "Pre-order ships within seven business days of the drop closing. Live drops ship within forty-eight hours.",
    },
    {
        h: "Tracking",
        p: "Każde zamówienie wysyłane jest z numerem śledzenia. Otrzymasz go mailem w chwili, gdy paczka opuszcza atelier. Operatorzy obsłudgi: InPost, DPD, DHL Express.",
    },
    {
        h: "Returns",
        p: "Trzydzieści dni na zwrot przez Wygodne Zwroty — wygeneruj etykietę z poziomu konta, wybierz paczkomat, oddaj paczkę. Zwroty środków w pięć dni roboczych od wpłynięcia paczki.",
    },
];

export default function Shipping() {
    return (
        <PageShell
            sectionNumber="09"
            eyebrow="Logistics · Made in Poland"
            title="Shipping"
            crumb="Shipping"
            data-testid="page-shipping"
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "clamp(48px, 6vw, 100px)",
                }}
            >
                {/* HIGHLIGHT BAND */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: easing.primary }}
                    data-testid="shipping-highlights"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                        gap: 0,
                        border: "1px solid var(--on-ink-line)",
                    }}
                >
                    {HIGHLIGHTS.map((h, i) => (
                        <div
                            key={h.label}
                            style={{
                                padding: "clamp(24px, 3vw, 40px)",
                                borderRight:
                                    i === 0
                                        ? "1px solid var(--on-ink-line)"
                                        : "none",
                                borderBottom: "1px solid transparent",
                                display: "flex",
                                flexDirection: "column",
                                gap: 14,
                            }}
                            data-testid={`shipping-highlight-${i}`}
                        >
                            <MonoLabel
                                tone="signal"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 10,
                                }}
                            >
                                <BlinkingDot size={6} />
                                {h.label}
                            </MonoLabel>
                            <div
                                style={{
                                    fontFamily: "var(--font-display)",
                                    fontWeight: 700,
                                    fontSize: "clamp(36px, 5vw, 72px)",
                                    letterSpacing: "-0.035em",
                                    lineHeight: 0.95,
                                    textTransform: "uppercase",
                                    color: "var(--on-ink)",
                                }}
                            >
                                {h.rate}
                            </div>
                            <p
                                style={{
                                    margin: 0,
                                    fontFamily: "var(--font-serif)",
                                    fontStyle: "italic",
                                    fontSize: 16,
                                    lineHeight: 1.55,
                                    color: "var(--on-ink-soft)",
                                    maxWidth: "40ch",
                                }}
                            >
                                {h.copy}
                            </p>
                        </div>
                    ))}
                </motion.div>

                {/* INTRO */}
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 0.85, ease: easing.primary }}
                    style={{
                        margin: 0,
                        fontFamily: "var(--font-serif)",
                        fontStyle: "italic",
                        fontSize: "clamp(20px, 2.2vw, 32px)",
                        lineHeight: 1.4,
                        color: "var(--on-ink)",
                        maxWidth: "34ch",
                    }}
                    data-testid="shipping-intro"
                >
                    Każda paczka opuszcza atelier na Próżnej z numerem
                    śledzenia w ciągu jednego dnia roboczego.
                </motion.p>

                {/* ZONES */}
                <section
                    data-testid="shipping-zones"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 26,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "space-between",
                            paddingBottom: 14,
                            borderBottom: "1px solid var(--on-ink-line)",
                            flexWrap: "wrap",
                            gap: 10,
                        }}
                    >
                        <h2
                            style={{
                                margin: 0,
                                fontFamily: "var(--font-display)",
                                fontWeight: 700,
                                fontSize: "clamp(28px, 3.2vw, 48px)",
                                letterSpacing: "-0.025em",
                                textTransform: "uppercase",
                                color: "var(--on-ink)",
                            }}
                        >
                            Zones &amp; Rates
                        </h2>
                        <MonoLabel tone="mute">
                            EUR · Calculated at checkout
                        </MonoLabel>
                    </div>

                    {ZONES.map((z, i) => (
                        <motion.article
                            key={z.n}
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.25 }}
                            transition={{
                                duration: 0.8,
                                delay: 0.06 * i,
                                ease: easing.primary,
                            }}
                            data-testid={`shipping-zone-${z.n}`}
                            style={{
                                display: "grid",
                                gridTemplateColumns:
                                    "minmax(0, 1.4fr) minmax(0, 1fr)",
                                gap: "clamp(24px, 4vw, 60px)",
                                padding: "clamp(24px, 3vw, 40px) 0",
                                borderBottom: "1px solid var(--on-ink-line)",
                                alignItems: "start",
                            }}
                            className="proof-grid-pdp"
                        >
                            {/* Left: zone label + countries */}
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 14,
                                }}
                            >
                                <MonoLabel tone="mute">
                                    [{z.n}] · {z.label}
                                </MonoLabel>
                                <div
                                    style={{
                                        fontFamily: "var(--font-display)",
                                        fontWeight: 700,
                                        fontSize: "clamp(28px, 3.6vw, 56px)",
                                        letterSpacing: "-0.03em",
                                        lineHeight: 1.0,
                                        textTransform: "uppercase",
                                        color: "var(--on-ink)",
                                    }}
                                >
                                    Zone {z.n}
                                </div>
                                <p
                                    style={{
                                        margin: 0,
                                        fontFamily: "var(--font-serif)",
                                        fontStyle: "italic",
                                        fontSize: 17,
                                        lineHeight: 1.55,
                                        color: "var(--on-ink-soft)",
                                        maxWidth: "48ch",
                                    }}
                                >
                                    {z.countries.join(", ")}
                                </p>
                                {z.note && (
                                    <MonoLabel tone="signal" size={10}>
                                        ↳ {z.note}
                                    </MonoLabel>
                                )}
                            </div>

                            {/* Right: rate + carrier + lead-time */}
                            <dl
                                style={{
                                    margin: 0,
                                    display: "grid",
                                    gridTemplateColumns: "auto 1fr",
                                    gap: "12px 22px",
                                    alignItems: "baseline",
                                }}
                            >
                                <MonoLabel tone="mute">Shipping rate</MonoLabel>
                                <dd
                                    style={{
                                        margin: 0,
                                        fontFamily: "var(--font-mono)",
                                        fontSize: "clamp(20px, 2.4vw, 32px)",
                                        letterSpacing: "-0.02em",
                                        color: "var(--on-ink)",
                                        fontWeight: 600,
                                    }}
                                >
                                    {z.rate}
                                </dd>
                                <MonoLabel tone="mute">Lead time</MonoLabel>
                                <dd
                                    style={{
                                        margin: 0,
                                        fontFamily: "var(--font-mono)",
                                        fontSize: 13,
                                        letterSpacing: "0.14em",
                                        textTransform: "uppercase",
                                        color: "var(--on-ink-soft)",
                                    }}
                                >
                                    {z.leadTime}
                                </dd>
                                <MonoLabel tone="mute">Carrier</MonoLabel>
                                <dd
                                    style={{
                                        margin: 0,
                                        fontFamily: "var(--font-mono)",
                                        fontSize: 13,
                                        letterSpacing: "0.14em",
                                        textTransform: "uppercase",
                                        color: "var(--on-ink-soft)",
                                    }}
                                >
                                    {z.carrier}
                                </dd>
                            </dl>
                        </motion.article>
                    ))}
                </section>

                {/* NOTES */}
                <section
                    data-testid="shipping-notes"
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(280px, 1fr))",
                        gap: "clamp(22px, 2.5vw, 40px)",
                    }}
                >
                    {NOTES.map((n, i) => (
                        <motion.div
                            key={n.h}
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{
                                duration: 0.75,
                                delay: 0.05 * i,
                                ease: easing.primary,
                            }}
                            data-testid={`shipping-note-${i}`}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 10,
                            }}
                        >
                            <MonoLabel tone="mute">[{String(i + 1).padStart(2, "0")}]</MonoLabel>
                            <h3
                                style={{
                                    margin: 0,
                                    fontFamily: "var(--font-display)",
                                    fontWeight: 700,
                                    fontSize: "clamp(20px, 1.9vw, 28px)",
                                    letterSpacing: "-0.02em",
                                    textTransform: "uppercase",
                                    color: "var(--on-ink)",
                                }}
                            >
                                {n.h}
                            </h3>
                            <p
                                style={{
                                    margin: 0,
                                    fontFamily: "var(--font-body)",
                                    fontSize: 14,
                                    lineHeight: 1.65,
                                    color: "var(--on-ink-soft)",
                                    maxWidth: "38ch",
                                }}
                            >
                                {n.p}
                            </p>
                        </motion.div>
                    ))}
                </section>

                {/* RETURNS CTA */}
                <div
                    data-testid="shipping-returns-cta"
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 18,
                        padding: "clamp(28px, 4vw, 48px) clamp(24px, 3vw, 40px)",
                        border: "1px solid var(--on-ink-line)",
                        background: "var(--on-ink-surface, rgba(255,255,255,0.03))",
                        flexWrap: "wrap",
                    }}
                >
                    <div style={{ maxWidth: "44ch" }}>
                        <MonoLabel tone="mute">— Wygodne Zwroty</MonoLabel>
                        <h3
                            style={{
                                margin: "6px 0 6px",
                                fontFamily: "var(--font-display)",
                                fontWeight: 700,
                                fontSize: "clamp(20px, 2vw, 30px)",
                                letterSpacing: "-0.02em",
                                textTransform: "uppercase",
                                color: "var(--on-ink)",
                            }}
                        >
                            Thirty days. No friction.
                        </h3>
                        <p
                            style={{
                                margin: 0,
                                fontFamily: "var(--font-serif)",
                                fontStyle: "italic",
                                fontSize: 16,
                                lineHeight: 1.55,
                                color: "var(--on-ink-soft)",
                            }}
                        >
                            Generate a return label from your account, drop the
                            parcel at the nearest paczkomat. Refunds clear within
                            five business days of arrival.
                        </p>
                    </div>
                    <FieldButton to="/policy/returns" data-testid="shipping-returns-policy">
                        Read Returns Policy
                    </FieldButton>
                </div>
            </div>
        </PageShell>
    );
}
